import cn from 'classnames'
import { useCallback, useMemo, useRef, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import { IModalContent, ModalDialog } from 'core-components/library/modalDialog'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'

import { applyPlanningSmb } from '../../api'
import {
  PLANNING_SMB_TRANSLATION_KEYS,
  SMB_FY_QUARTER,
  SMB_PERCENT_FRACTION_DIGITS,
  SMB_PERCENT_STEP,
} from '../../configs'
import {
  SMB_GUIDED_LEGACY_PERIOD_TEXT,
  SMB_GUIDED_WIZARD_TEXT,
} from '../../configs/guidedWizardTranslations'
import {
  buildGuidedSmbPlanRows,
  getGuidedSmbClassification,
  getGuidedSmbInitialDelta,
  getGuidedSmbPlanResult,
  getGuidedSmbTrend,
  getPlanningSmbYears,
  getSmbEffectiveValue,
  hasGenuineDirectSplit,
  isSmbNodeExcluded,
  mapPlanningSmbToStore,
  preservePlanningSmbUnshownYears,
  TGetSmbWeight,
  TGuidedSmbClassification,
  TGuidedSmbTrend,
} from '../../lib'
import { IPlanningSmb, ISmbNode, TSmbSplitResult } from '../../model/types'
import {
  PlanningSmbGuidedText,
  usePlanningSmbGuidedTranslation,
} from '../planningSmbGuidedWizard/components/PlanningSmbGuidedText'

import styles from './PlanningSmbGuidedModal.module.scss'

const KEYS = PLANNING_SMB_TRANSLATION_KEYS

type TGuidedScreen = 'congratulations' | 'plan'

export interface IPlanningSmbGuidedContentProps {
  tree: ISmbNode
  planningSmb: IPlanningSmb[]
  partnerId: string
  fYear: number
  lastPlanYear?: number
  programType: string
  offeringSummary: TSmbSplitResult
  offeringBaseline: TSmbSplitResult
  getWeight: TGetSmbWeight
  getBaselineValue: (node: ISmbNode) => TSmbSplitResult
  onSaved: () => void | Promise<void>
  onSaveFailed: (error: unknown) => void
  onSwitchClassic: () => void
  onBusyChange?: (isBusy: boolean) => void
}

export interface IPlanningSmbGuidedModalProps extends IPlanningSmbGuidedContentProps {
  show: boolean
  partnerName: string
  onClose: () => void
}

const GuidedTrendArrow = ({ trend }: { trend: TGuidedSmbTrend }) =>
  trend ? (
    <span
      className={cn(styles.trendArrow, trend === 'up' ? styles.trendUp : styles.trendDown)}
      aria-hidden
    >
      {trend === 'up' ? '▲' : '▼'}
    </span>
  ) : null

const GuidedClassification = ({ classification }: { classification: TGuidedSmbClassification }) => {
  const key = {
    smb: KEYS.guided.smbFocused,
    eu: KEYS.guided.euFocused,
    neutral: KEYS.guided.notFocused,
  }[classification]

  return (
    <span
      className={cn(styles.classification, {
        [styles.classificationSmb]: classification === 'smb',
        [styles.classificationEu]: classification === 'eu',
        [styles.classificationNeutral]: classification === 'neutral',
      })}
    >
      <Translation span text={key} />
    </span>
  )
}

interface IGuidedSplitTileProps {
  split: Exclude<TSmbSplitResult, { isEmpty: true }>
  isPlanned?: boolean
  baseline?: Exclude<TSmbSplitResult, { isEmpty: true }>
}

const GuidedSplitTile = ({ split, isPlanned, baseline }: IGuidedSplitTileProps) => {
  const classification = getGuidedSmbClassification(split)
  const smbTrend = baseline ? getGuidedSmbTrend(split.smb, baseline.smb) : null
  const euTrend = baseline ? getGuidedSmbTrend(split.eu, baseline.eu) : null

  return (
    <div className={cn(styles.splitTile, { [styles.plannedTile]: isPlanned })}>
      <div>
        <div className={cn(styles.statNumber, styles.smb)}>
          {split.smb.toFixed(SMB_PERCENT_FRACTION_DIGITS)}%
          <GuidedTrendArrow trend={smbTrend} />
        </div>
        <div className={styles.statLabel}>
          <Translation span text={isPlanned ? KEYS.guided.smbPlanned : KEYS.guided.smbBaseline} />
        </div>
      </div>
      <div className={styles.bigBar} aria-hidden>
        <span className={styles.barSmb} style={{ width: `${split.smb}%` }} />
        <span className={styles.barEu} style={{ width: `${split.eu}%` }} />
      </div>
      <div>
        <div className={cn(styles.statNumber, styles.eu)}>
          {split.eu.toFixed(SMB_PERCENT_FRACTION_DIGITS)}%
          <GuidedTrendArrow trend={euTrend} />
        </div>
        <div className={styles.statLabel}>
          <Translation span text={isPlanned ? KEYS.guided.euPlanned : KEYS.guided.euBaseline} />
        </div>
      </div>
      {classification && <GuidedClassification classification={classification} />}
    </div>
  )
}

export const PlanningSmbGuidedContent = ({
  tree,
  planningSmb,
  partnerId,
  fYear,
  lastPlanYear,
  programType,
  offeringSummary,
  offeringBaseline,
  getWeight,
  getBaselineValue,
  onSaved,
  onSaveFailed,
  onSwitchClassic,
  onBusyChange,
  closeModal,
}: IPlanningSmbGuidedContentProps & IModalContent) => {
  const t = useSafetyTranslation()
  const translate = usePlanningSmbGuidedTranslation()
  const years = useMemo(() => getPlanningSmbYears(fYear, lastPlanYear), [fYear, lastPlanYear])
  const context = useMemo(
    () => ({ partnerId, programType, years }),
    [partnerId, programType, years]
  )
  const initialStore = useMemo(
    () => mapPlanningSmbToStore(planningSmb, context, tree),
    [planningSmb, context, tree]
  )
  const referenceSmb = !offeringBaseline.isEmpty
    ? offeringBaseline.smb
    : !offeringSummary.isEmpty
      ? offeringSummary.smb
      : 50
  const initialDirectPlannedSmb = useMemo(() => {
    for (const year of years) {
      if (!hasGenuineDirectSplit(initialStore, tree.id, year)) {
        continue
      }

      const value = getSmbEffectiveValue(initialStore, tree, year, SMB_FY_QUARTER, getWeight, {
        deriveFyFromQuarters: year === fYear,
      })
      if (!value.isEmpty) {
        return value.smb
      }
    }

    return undefined
  }, [fYear, getWeight, initialStore, tree, years])
  const [screen, setScreen] = useState<TGuidedScreen>(
    offeringSummary.isEmpty ? 'congratulations' : 'plan'
  )
  const [cameFromCongratulations] = useState(offeringSummary.isEmpty)
  const [categoriesExpanded, setCategoriesExpanded] = useState(false)
  const [deltaInput, setDeltaInput] = useState(
    () => `${getGuidedSmbInitialDelta(referenceSmb, initialDirectPlannedSmb)}`
  )
  const [isApplying, setIsApplying] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [confirmationYear, setConfirmationYear] = useState<number | null>(null)
  const confirmationResolver = useRef<((confirmed: boolean) => void) | null>(null)

  const delta = Number(deltaInput) || 0
  const planned = getGuidedSmbPlanResult(referenceSmb, delta)
  const categories = useMemo(
    () => tree.children.filter((category) => !isSmbNodeExcluded(category)),
    [tree]
  )

  const requestOverwriteConfirmation = useCallback(
    (year: number) =>
      new Promise<boolean>((resolve) => {
        confirmationResolver.current = resolve
        setConfirmationYear(year)
      }),
    []
  )
  const resolveOverwriteConfirmation = useCallback((confirmed: boolean) => {
    const resolve = confirmationResolver.current
    confirmationResolver.current = null
    setConfirmationYear(null)
    resolve?.(confirmed)
  }, [])

  const setBusy = useCallback(
    (isBusy: boolean) => {
      setIsApplying(isBusy)
      onBusyChange?.(isBusy)
    },
    [onBusyChange]
  )

  const handleApply = useCallback(async () => {
    setBusy(true)
    try {
      const result = await buildGuidedSmbPlanRows({
        planningSmb,
        tree,
        partnerId,
        programType,
        years,
        smb: planned.smb,
        getWeight,
        confirmOverwrite: requestOverwriteConfirmation,
      })

      if (result.hasChanges) {
        setIsSaving(true)
        await applyPlanningSmb({
          partnerId,
          fYear,
          rows: preservePlanningSmbUnshownYears(result.rows, planningSmb, {
            partnerId,
            programType,
            fYear,
            lastPlanYear,
          }),
        })
        await onSaved()
      }

      setIsSaving(false)
      setBusy(false)
      closeModal()
    } catch (error) {
      setIsSaving(false)
      setBusy(false)
      onSaveFailed(error)
    }
  }, [
    closeModal,
    fYear,
    lastPlanYear,
    getWeight,
    onSaveFailed,
    onSaved,
    partnerId,
    planned.smb,
    planningSmb,
    programType,
    requestOverwriteConfirmation,
    setBusy,
    tree,
    years,
  ])

  const baselineYear = `${fYear - 1}`.slice(-2)
  const isShortHorizon = years.length < 3
  const formatYear = (year: number) =>
    translate(SMB_GUIDED_WIZARD_TEXT.fiscalYear, { year: String(year).slice(-2) })
  const periodParams = {
    baselineYear,
    programType,
    planPeriod:
      years.length === 1
        ? formatYear(fYear)
        : `${formatYear(fYear)}\u2013${formatYear(years[years.length - 1])}`,
  }
  const planYearParams = [
    { key: '{baselineYear}', value: baselineYear },
    { key: '{fYear}', value: `${fYear}`.slice(-2) },
    { key: '{fYearNext}', value: `${fYear + 1}`.slice(-2) },
    { key: '{fYearNextTwo}', value: `${fYear + 2}`.slice(-2) },
    { key: '{programType}', value: programType },
  ]

  return (
    <div className={styles.content} aria-busy={isApplying}>
      <div className={styles.body}>
        {screen === 'congratulations' ? (
          <div className={styles.congratulations}>
            <div className={styles.congratulationsBadge} aria-hidden>
              ✓
            </div>
            <div className={styles.congratulationsTitle}>
              {isShortHorizon ? (
                <PlanningSmbGuidedText
                  text={SMB_GUIDED_LEGACY_PERIOD_TEXT.congratulationsTitle}
                  params={periodParams}
                />
              ) : (
                <Translation
                  span
                  text={KEYS.guided.congratulationsTitle}
                  replaceParams={planYearParams}
                />
              )}
            </div>
            <div className={styles.congratulationsBody}>
              {isShortHorizon ? (
                <PlanningSmbGuidedText
                  text={SMB_GUIDED_LEGACY_PERIOD_TEXT.congratulationsBody}
                  params={periodParams}
                />
              ) : (
                <Translation
                  span
                  text={KEYS.guided.congratulationsBody}
                  replaceParams={planYearParams}
                />
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.stepTitle}>
              <Translation span text={KEYS.guided.reviewTitle} replaceParams={planYearParams} />
            </div>
            <div className={styles.stepNote}>
              {isShortHorizon ? (
                <PlanningSmbGuidedText
                  text={SMB_GUIDED_LEGACY_PERIOD_TEXT.reviewNote}
                  params={periodParams}
                />
              ) : (
                <Translation span text={KEYS.guided.reviewNote} replaceParams={planYearParams} />
              )}
            </div>

            {offeringBaseline.isEmpty ? (
              <div className={styles.noBaseline}>
                <Translation
                  span
                  text={KEYS.guided.noOfferingBaseline}
                  replaceParams={[{ key: '{baselineYear}', value: baselineYear }]}
                />
              </div>
            ) : (
              <GuidedSplitTile split={offeringBaseline} />
            )}

            <button
              type="button"
              className={styles.categoryToggle}
              onClick={() => setCategoriesExpanded((expanded) => !expanded)}
            >
              <span aria-hidden>{categoriesExpanded ? '▾' : '▸'}</span>
              <Translation
                span
                text={
                  categoriesExpanded
                    ? KEYS.guided.hideCategoryBreakdown
                    : KEYS.guided.showCategoryBreakdown
                }
              />
            </button>

            {categoriesExpanded && (
              <div className={styles.categoryBreakdown}>
                {categories.map((category) => {
                  const baseline = getBaselineValue(category)

                  return (
                    <div className={styles.categoryRow} key={category.id}>
                      <div className={styles.categoryName}>
                        <Translation span text={category.nameKey} />
                      </div>
                      {baseline.isEmpty ? (
                        <div className={styles.categoryNoBaseline}>
                          <Translation
                            span
                            text={KEYS.guided.noCategoryBaseline}
                            replaceParams={[{ key: '{baselineYear}', value: baselineYear }]}
                          />
                        </div>
                      ) : (
                        <>
                          <div className={styles.categoryBar} aria-hidden>
                            <span
                              className={styles.barSmbMuted}
                              style={{ width: `${baseline.smb}%` }}
                            />
                            <span
                              className={styles.barEuMuted}
                              style={{ width: `${baseline.eu}%` }}
                            />
                          </div>
                          <span className={styles.categoryShift}>
                            <Translation
                              span
                              text={KEYS.guided.categoryShift}
                              replaceParams={[
                                {
                                  key: '{smb}',
                                  value: planned.smb.toFixed(SMB_PERCENT_FRACTION_DIGITS),
                                },
                                {
                                  key: '{shift}',
                                  value: `${planned.smb - baseline.smb >= 0 ? '+' : ''}${(
                                    planned.smb - baseline.smb
                                  ).toFixed(SMB_PERCENT_FRACTION_DIGITS)}`,
                                },
                              ]}
                            />
                          </span>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <div className={styles.yoyTile}>
              <label className={styles.yoyLabel} htmlFor="planning-smb-guided-delta">
                <Translation span text={KEYS.guided.yoyLabel} />
              </label>
              <div className={styles.deltaField}>
                <input
                  id="planning-smb-guided-delta"
                  type="number"
                  step={SMB_PERCENT_STEP}
                  value={deltaInput}
                  disabled={isApplying}
                  aria-label={t(KEYS.guided.yoyLabel)}
                  onChange={(event) => setDeltaInput(event.target.value)}
                />
                <span className={styles.deltaSuffix}>
                  <Translation span text={KEYS.guided.yoySuffix} />
                </span>
              </div>
              {planned.isClamped && (
                <span className={styles.clampedNote}>
                  <Translation span text={KEYS.guided.clamped} />
                </span>
              )}
            </div>

            {!offeringBaseline.isEmpty && (
              <GuidedSplitTile
                isPlanned
                split={{ isEmpty: false, smb: planned.smb, eu: planned.eu }}
                baseline={offeringBaseline}
              />
            )}
          </>
        )}
      </div>

      <div className={styles.foot}>
        <div className={styles.footNote}>
          {screen === 'plan' && (
            <button
              type="button"
              className={styles.switchClassic}
              disabled={isApplying}
              onClick={onSwitchClassic}
            >
              <Translation span text={KEYS.guided.switchClassic} />
            </button>
          )}
        </div>
        <div className={styles.footActions}>
          {screen === 'congratulations' ? (
            <>
              <Button variant="outlined" color="grey" onClick={closeModal}>
                <Translation span text={KEYS.guided.notNow} />
              </Button>
              <Button color="primary" onClick={() => setScreen('plan')}>
                <Translation span text={KEYS.guided.proceed} />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" color="grey" disabled={isApplying} onClick={closeModal}>
                <Translation span text={KEYS.modal.cancel} />
              </Button>
              {cameFromCongratulations && (
                <Button
                  variant="outlined"
                  color="grey"
                  disabled={isApplying}
                  onClick={() => setScreen('congratulations')}
                >
                  <Translation span text={KEYS.guided.back} />
                </Button>
              )}
              <Button
                color="primary"
                disabled={isApplying}
                loading={isSaving}
                onClick={handleApply}
              >
                <Translation span text={KEYS.guided.applyPlan} />
              </Button>
            </>
          )}
        </div>
      </div>

      <LoaderOverlay className={styles.savingOverlay} show={isSaving} />

      {confirmationYear !== null && (
        <ConfirmationDialog
          isVisible
          title={KEYS.confirm.title}
          confirmBtnText={KEYS.confirm.apply}
          rejectBtnText={KEYS.confirm.cancel}
          onConfirm={() => resolveOverwriteConfirmation(true)}
          onReject={() => resolveOverwriteConfirmation(false)}
          content={
            <Translation
              text={KEYS.guided.overwriteBody}
              replaceParams={[
                { key: '{programType}', value: programType },
                { key: '{fYear}', value: `${confirmationYear}`.slice(-2) },
              ]}
            />
          }
        />
      )}
    </div>
  )
}

export const PlanningSmbGuidedModal = ({
  show,
  partnerName,
  programType,
  onClose,
  ...contentProps
}: IPlanningSmbGuidedModalProps) => {
  const isBusyRef = useRef(false)
  const handleBusyChange = useCallback((isBusy: boolean) => {
    isBusyRef.current = isBusy
  }, [])
  const handleClose = useCallback(() => {
    if (!isBusyRef.current) {
      onClose()
    }
  }, [onClose])

  return (
    <ModalDialog<IPlanningSmbGuidedContentProps>
      show={show}
      size="md"
      classes={{
        window: styles.window,
        header: styles.modalHeader,
        headerBodyContent: styles.headerBody,
        body: styles.modalBody,
        closeButton: styles.closeButton,
      }}
      closeOnOutsideClick={false}
      header={() => (
        <div className={styles.header}>
          <span className={styles.crumb}>
            {partnerName}
            {' · '}
            {programType}
            {' · '}
            <Translation span text={KEYS.guided.crumb} />
          </span>
          <span className={styles.title}>
            <Translation span text={KEYS.guided.title} />
          </span>
        </div>
      )}
      content={PlanningSmbGuidedContent}
      contentProps={{
        ...contentProps,
        programType,
        onBusyChange: handleBusyChange,
      }}
      onClose={handleClose}
    />
  )
}
